import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faVideo, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import { onSocket } from '../../utils/socket';
import EmployeeAvatar from '../EmployeeAvatar';
import GroupIcon from './GroupIcon';
import { describeCall } from './MessageBubble';
import WithPresence from '../PresenceDot';

/**
 * The Calls tab — every call this person has been in, newest first.
 *
 * Reads from /api/calls/mine rather than being assembled out of the call lines
 * in each thread: the log has to span conversations, and collecting it from
 * messages would mean loading every conversation the person is in to answer
 * "who did I speak to last week".
 *
 * The call lines in the threads and this list are two views of the same Call
 * documents, and they share describeCall() so a call cannot read as "Incoming"
 * in one place and "Outgoing" in the other.
 */

const dayLabel = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
        return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';

    // Inside the last week the weekday is more useful than the date.
    if (now - d < 7 * 24 * 60 * 60 * 1000) {
        return d.toLocaleDateString('en-GB', { weekday: 'short' });
    }
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const CallList = ({ myId, query = '', onOpenConversation, onCall }) => {
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/calls/mine', { params: { limit: 40 } });
            setCalls(res.data.data || []);
            setHasMore(Boolean(res.data.hasMore));
        } catch {
            setCalls([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    /*
     * A call that ends while this tab is open should appear without a refresh.
     *
     * Refetching rather than splicing the new call in: the row needs the
     * participants populated, which the socket event does not carry. Safe to
     * fetch immediately - the server closes the Call row and writes the thread
     * entry before it emits call:ended, so the row is already there.
     */
    useEffect(() => onSocket('call:ended', load), [load]);

    const loadMore = async () => {
        const oldest = calls[calls.length - 1];
        if (!oldest || loadingMore) return;
        setLoadingMore(true);
        try {
            const res = await api.get('/calls/mine', {
                params: { limit: 40, before: oldest.createdAt }
            });
            setCalls((prev) => [...prev, ...(res.data.data || [])]);
            setHasMore(Boolean(res.data.hasMore));
        } catch {
            /* leave what is already on screen */
        } finally {
            setLoadingMore(false);
        }
    };

    if (loading) return <div className="msgr-list"><div className="msgr-empty">Loading…</div></div>;

    if (!calls.length) {
        return (
            <div className="msgr-list">
                <div className="msgr-empty">
                    No calls yet. Open a chat and press the phone icon to make one.
                </div>
            </div>
        );
    }

    const rows = calls.filter((call) => {
        if (!query) return true;
        const other = call.participants?.find((p) => String(p.user?._id) !== String(myId));
        const name = call.conversationId?.kind === 'group'
            ? call.conversationId?.name
            : other?.user?.name;
        return (name || '').toLowerCase().includes(query.toLowerCase());
    });

    if (!rows.length) {
        return <div className="msgr-list"><div className="msgr-empty">No calls match that.</div></div>;
    }

    return (
        <div className="msgr-list">
            {rows.map((call) => {
                const isGroup = call.conversationId?.kind === 'group';
                const other = call.participants?.find(
                    (p) => String(p.user?._id) !== String(myId)
                )?.user;

                const outgoing = String(call.initiator?._id || call.initiator) === String(myId);
                /*
                 * Shaped to match what the thread bubble reads off
                 * systemEvent.call, so the same call cannot be described one way
                 * in the transcript and another way here. The Call document
                 * carries the roster rather than the counts, so they are derived.
                 */
                const read = describeCall({
                    ...call,
                    isGroup: (call.participants?.length || 0) > 2,
                    joinedCount: (call.participants || [])
                        .filter((p) => p.state === 'left' || p.state === 'joined').length,
                    participantCount: call.participants?.length || 0
                }, outgoing);

                const title = isGroup
                    ? (call.conversationId?.name || 'Group call')
                    : (other?.name || 'Unknown');

                return (
                    <div
                        key={call._id}
                        className="msgr-row msgr-call-row"
                        onClick={() => onOpenConversation?.(call.conversationId?._id || call.conversationId)}
                    >
                        {isGroup ? (
                            <GroupIcon
                                conversation={call.conversationId}
                                className="msgr-avatar"
                            />
                        ) : (
                            <WithPresence userId={other?._id}>
                                <EmployeeAvatar person={other} className="msgr-avatar" />
                            </WithPresence>
                        )}

                        <div className="msgr-row-body">
                            <div className="msgr-row-top">
                                <strong className={read.missed ? 'is-missed' : ''}>{title}</strong>
                                <span className="msgr-row-time">{dayLabel(call.createdAt)}</span>
                            </div>
                            <div className="msgr-row-sub">
                                <FontAwesomeIcon
                                    className={`msgr-call-dir${read.missed ? ' is-missed' : ''}`}
                                    icon={outgoing ? faArrowUp : faArrowDown}
                                />
                                <span>{read.label}</span>
                                {read.detail && <span className="msgr-call-dur">{read.detail}</span>}
                            </div>
                        </div>

                        {/*
                          * Call back, without having to open the thread first —
                          * the reason anybody opens a call log. A group row calls
                          * the group; a direct row calls the person. Both go
                          * through the same conversation id, so neither needs to
                          * know anything about rosters.
                          */}
                        {(isGroup || other) && (
                            <button
                                className="msgr-icon-btn msgr-call-btn"
                                title={`Call ${title}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCall?.(
                                        call.conversationId?._id || call.conversationId,
                                        other,
                                        { isGroup, title }
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={call.kind === 'video' ? faVideo : faPhone} />
                            </button>
                        )}
                    </div>
                );
            })}

            {hasMore && (
                <button className="msgr-more" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? 'Loading…' : 'Older calls'}
                </button>
            )}
        </div>
    );
};

export default CallList;
